import { Navigate, useParams } from 'react-router-dom';
import { Role } from '../types/auth.type';
import { useAuthStore } from '../store/auth.store';
import { hasGlobalOrgAccess } from '../utils/roles.utils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles: Role[];
  checkOrganization?: boolean;
  customPermission?: 'access_invoices' | 'access_reports' | 'access_chat';
}

export default function ProtectedRoute({ 
  children, 
  requiredRoles, 
  checkOrganization = false,
  customPermission
}: ProtectedRouteProps) {
  const { user, isInitialized } = useAuthStore();
  const { organizationId } = useParams<{ organizationId: string }>();

  if (!isInitialized) {
    return <></>;
  }
  // Only redirect if user is truly null (not just temporarily missing due to a fetch error)
  if (user === null) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.data.role || !requiredRoles.includes(user.data.role)) {
    return <Navigate to="/not-authorized" replace />;
  }

  if (checkOrganization && !hasGlobalOrgAccess(user.data.role)){
    if (!organizationId) {
        return <Navigate to="/not-found" replace />;
      }
    // Allow organization users and auditors to access their own organizations
    if (
      user.data.role !== Role.ORGANIZATION
      && user.data.role !== Role.ORGANIZATION_USER
      && user.data.role !== Role.AUDITOR
      && !user.data.access.some(access => access.organization_id === organizationId)
    ) {
        return <Navigate to="/not-authorized" replace />;
      }
  }

  // Auditor: fixed role permissions (read-only invoices + approved reports)
  if (customPermission && user.data.role === Role.AUDITOR) {
    if (customPermission === 'access_chat') {
      return <Navigate to="/not-authorized" replace />;
    }
    return <>{children}</>;
  }

  // Check custom permissions for ORGANIZATION_USER
  if (customPermission && user.data.role === Role.ORGANIZATION_USER && organizationId) {
    const orgAccess = user.data.access.find(access => access.organization_id === organizationId);
    const customPermissions = orgAccess?.custom_permissions || [];
    
    // access_invoices is default for all ORGANIZATION_USER - always allowed
    const hasPermission = customPermission === 'access_invoices' 
      ? true 
      : customPermissions.includes(customPermission);
    
    if (!hasPermission) {
      return <Navigate to="/not-authorized" replace />;
    }
  }

  return <>{children}</>;
} 